import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { TID } from '../constants/testIds';
import NeighbourhoodCard from '../components/common/NeighbourhoodCard';
import MapView from '../components/common/MapView';
import SEOHead from '../components/common/SEOHead';
import { Search, Map as MapIcon, LayoutGrid } from 'lucide-react';

const CITY_OPTIONS = [
    'All cities', 'Whitby', 'Oshawa', 'Pickering', 'Ajax',
    'Hamilton', 'Burlington', 'Oakville', 'Milton', 'Halton Hills',
    'Brantford', 'Paris', 'St. George',
    'Niagara-on-the-Lake', 'St. Catharines', 'Grimsby', 'Welland', 'Pelham',
];
const REGION_OPTIONS = ['All regions', 'Durham Region', 'Hamilton', 'Halton Region', 'Brantford', 'Brant County', 'Niagara Region'];
const TAG_OPTIONS = ['Any tag', 'family', 'walkable', 'commuter', 'retiree', 'investment', 'affordable', 'luxury', 'heritage', 'small-town', 'schools', 'waterfront', 'detached', 'new-build'];
const SORT_OPTIONS = [
    { value: 'name', label: 'A–Z' },
    { value: 'price_asc', label: 'Price ↑' },
    { value: 'price_desc', label: 'Price ↓' },
    { value: 'forecast', label: 'Forecast 5Y' },
    { value: 'walkability', label: 'Walkability' },
];

export default function Neighbourhoods() {
    const [searchParams] = useSearchParams();
    const initTag = TAG_OPTIONS.includes(searchParams.get('tag')) ? searchParams.get('tag') : 'Any tag';
    const initRegion = REGION_OPTIONS.includes(searchParams.get('region')) ? searchParams.get('region') : 'All regions';
    const initCity = CITY_OPTIONS.includes(searchParams.get('city')) ? searchParams.get('city') : 'All cities';
    const initSort = SORT_OPTIONS.some((o) => o.value === searchParams.get('sort')) ? searchParams.get('sort') : 'name';

    const [q, setQ] = useState('');
    const [region, setRegion] = useState(initRegion);
    const [city, setCity] = useState(initCity);
    const [tag, setTag] = useState(initTag);
    const [sort, setSort] = useState(initSort);
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [view, setView] = useState('grid'); // 'grid' | 'map'

    const params = useMemo(() => {
        const p = { sort };
        if (region !== 'All regions') p.region = region;
        if (city !== 'All cities') p.city = city;
        if (tag !== 'Any tag') p.tag = tag;
        if (q) p.q = q;
        if (minPrice) p.min_price = Number(minPrice);
        if (maxPrice) p.max_price = Number(maxPrice);
        return p;
    }, [q, region, city, tag, sort, minPrice, maxPrice]);

    const { data, isLoading } = useQuery({
        queryKey: ['neighbourhoods', params],
        queryFn: async () => (await api.get('/neighbourhoods', { params })).data,
    });

    return (
        <div>
            <SEOHead
                title="Southern Ontario neighbourhoods"
                description="Explore every neighbourhood across Durham, Hamilton, Halton, Brant and Niagara. Filter by region, price, schools, walkability, and 5-year forecast."
                path="/neighbourhoods"
            />
            <section className="border-b border-neutral-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-10">
                    <div className="overline">Explore · Southern Ontario</div>
                    <h1 className="font-editorial text-[44px] lg:text-[64px] tracking-editorial mt-4 leading-[1.04]">
                        Every neighbourhood. Ranked, filtered, honestly.
                    </h1>
                    <p className="mt-6 text-[15px] text-neutral-600 max-w-2xl">
                        Durham, Hamilton, Burlington, Oakville, Milton, Halton Hills, Brantford, Brant County and Niagara. Filter by region, city, price, or lifestyle tag.
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section className="border-b border-neutral-200 bg-white sticky top-16 z-30">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 grid grid-cols-2 lg:grid-cols-12 gap-3">
                    <div className="col-span-2 lg:col-span-3 flex items-center gap-2 border border-neutral-300 px-3 h-11">
                        <Search className="w-4 h-4 text-neutral-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search neighbourhood, city, postal code"
                            data-testid={TID.search.input}
                            className="flex-1 outline-none text-[14px] bg-transparent"
                        />
                    </div>
                    <select value={region} onChange={(e) => setRegion(e.target.value)}
                        data-testid="search-region"
                        className="col-span-1 lg:col-span-2 border border-neutral-300 px-3 h-11 text-[14px] bg-white">
                        {REGION_OPTIONS.map((r) => <option key={r}>{r}</option>)}
                    </select>
                    <select value={city} onChange={(e) => setCity(e.target.value)} data-testid={TID.search.city}
                        className="col-span-1 lg:col-span-2 border border-neutral-300 px-3 h-11 text-[14px] bg-white">
                        {CITY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <select value={tag} onChange={(e) => setTag(e.target.value)} data-testid={TID.search.tag}
                        className="col-span-1 lg:col-span-2 border border-neutral-300 px-3 h-11 text-[14px] bg-white">
                        {TAG_OPTIONS.map((t) => <option key={t}>{t}</option>)}
                    </select>
                    <div className="col-span-2 lg:col-span-2 grid grid-cols-2 gap-1">
                        <input type="number" placeholder="Min $" value={minPrice} onChange={(e) => setMinPrice(e.target.value)}
                            data-testid={TID.search.priceMin}
                            className="border border-neutral-300 px-3 h-11 text-[14px]" />
                        <input type="number" placeholder="Max $" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)}
                            data-testid={TID.search.priceMax}
                            className="border border-neutral-300 px-3 h-11 text-[14px]" />
                    </div>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid={TID.search.sort}
                        className="col-span-2 lg:col-span-1 border border-neutral-300 px-3 h-11 text-[14px] bg-white">
                        {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>
            </section>

            {/* Results */}
            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                    <div className="flex items-center justify-between mb-6">
                        <div className="overline">{data?.count ?? '—'} results</div>
                        <div className="flex items-center gap-0 border border-neutral-300">
                            <button
                                onClick={() => setView('grid')}
                                data-testid="view-grid"
                                className={`px-3 py-1.5 text-[12px] flex items-center gap-1.5 ${view === 'grid' ? 'bg-brand-navy text-white' : 'bg-white text-neutral-600 hover:text-black'}`}
                            >
                                <LayoutGrid className="w-3.5 h-3.5" /> Grid
                            </button>
                            <button
                                onClick={() => setView('map')}
                                data-testid="view-map"
                                className={`px-3 py-1.5 text-[12px] flex items-center gap-1.5 border-l border-neutral-300 ${view === 'map' ? 'bg-brand-navy text-white' : 'bg-white text-neutral-600 hover:text-black'}`}
                            >
                                <MapIcon className="w-3.5 h-3.5" /> Map
                            </button>
                        </div>
                    </div>
                    {isLoading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="border border-neutral-200 aspect-[4/5] bg-neutral-50" />
                            ))}
                        </div>
                    ) : !data?.items?.length ? (
                        <div data-testid={TID.search.emptyState} className="border border-neutral-200 py-24 text-center text-neutral-500">
                            No neighbourhoods match those filters yet.
                        </div>
                    ) : view === 'map' ? (
                        <MapView items={data.items} height={620} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {data.items.map((n) => (
                                <div key={n.slug} data-testid={TID.search.card}>
                                    <NeighbourhoodCard n={n} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
